import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'

import FeatureBlock from '@/components/feature-block'
import Footer from '@/components/footer'
import Header from '@/components/header'
import InlineWistiaVideo from '@/components/inline-wistia-video'
import SEO from '@/components/seo'
import {TypeformSurvey} from '@/components/typeform-survey'
import type {AboutQuery} from '@/graphql-types'
import SignupBannerSection from '@/sections/signup-banner'
import TitleWithIconGridSection from '@/sections/title-with-icon-grid'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import Button from '@helios/shared/components/button'
import PrismicLink from '@helios/shared/components/prismic-link'
import {Main} from '@helios/shared/components/skip-to-content'
import {Section, BackgroundWrapper} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import getEnvironmentFormId from '@helios/shared/utils/get-environment-field'
import hasNonEmptyItems from '@helios/shared/utils/has-non-empty-items'

import * as s from './about.styles'

type Props = PageProps<AboutQuery>

const About = (props: Props) => {
    const data = props.data.prismicAboutUsPage.data

    if (!data) {
        return handleNoPrismicData()
    }

    return (
        <>
            <Header />

            <TypeformSurvey />

            <Main>
                <BackgroundWrapper color="muted">
                    <Section padding="small">
                        <FeatureBlock {...{...data.hero[0], body: data.hero[0].body.richText}} />
                    </Section>
                </BackgroundWrapper>

                {data.social_proof_logos_section && (
                    <TitleWithIconGridSection
                        title={data.social_proof_logos_section.document.data.title}
                        items={data.social_proof_logos_section.document.data.logos.map((item) => ({
                            icon: item.logo,
                        }))}
                    />
                )}

                {hasNonEmptyItems(data.feature_blocks_1) &&
                    data.feature_blocks_1.map((block, i) => (
                        <Section key={`${block.title}-${i}`}>
                            <FeatureBlock
                                image={block.image}
                                title={block.title}
                                body={block.body.richText}
                                mirrored={!(i % 2)}
                            />
                        </Section>
                    ))}

                {hasNonEmptyItems(data.inline_video) && (
                    <Section>
                        <s.VideoTitle>
                            <Text variant="heading-large">{data.inline_video[0].title}</Text>
                        </s.VideoTitle>

                        <InlineWistiaVideo
                            videoId={data.inline_video[0].video_id}
                            thumbnail={data.inline_video[0].video_thumbnail}
                        />
                    </Section>
                )}

                {hasNonEmptyItems(data.feature_blocks_2) &&
                    data.feature_blocks_2.map((block, i) => (
                        <Section key={`${block.title}-${i}`}>
                            <FeatureBlock
                                image={block.image}
                                title={block.title}
                                body={block.body.richText}
                                mirrored={!!(i % 2)}
                            >
                                <Button variant="outline" as={PrismicLink} {...block.cta_link}>
                                    {block.cta_text}
                                </Button>
                            </FeatureBlock>
                        </Section>
                    ))}

                {data.social_proof_logos_extended_section && (
                    <TitleWithIconGridSection
                        titleVariant="heading-large"
                        highlightColor="yellow"
                        title={
                            data.social_proof_logos_extended_section.document.data.title.richText
                        }
                        items={data.social_proof_logos_extended_section.document.data.logos.map(
                            (item) => ({
                                icon: item.logo,
                            })
                        )}
                    />
                )}

                {data.social_proof_ratings_section && (
                    <TitleWithIconGridSection
                        titleVariant="heading-large"
                        highlightColor="pink"
                        title={data.social_proof_ratings_section.document.data.title.richText}
                        items={data.social_proof_ratings_section.document.data.ratings.map(
                            (item) => ({
                                icon: item.rating.document.data.logo,
                                stars: item.rating.document.data.rating_number,
                                link: item.rating.document.data.link,
                            })
                        )}
                    />
                )}

                {data.signup_banner && (
                    <SignupBannerSection
                        {...data.signup_banner.document.data}
                        color="pink"
                        formId={getEnvironmentFormId(
                            data.signup_banner.document.data.hubspot_form_id_production,
                            data.signup_banner.document.data.hubspot_form_id_staging
                        )}
                        placeholder={data.signup_banner.document.data.email_input_placeholder}
                        submitText={data.signup_banner.document.data.submit_text}
                        errorText={data.signup_banner.document.data.email_input_error_text}
                    />
                )}
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<AboutQuery>) => {
    const pageData = data.prismicAboutUsPage.data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

export const query = graphql`
    query About($locale: String!) {
        prismicAboutUsPage(lang: {eq: $locale}) {
            _previewable
            data {
                meta_title
                meta_description
                open_graph_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                hero {
                    title
                    body {
                        richText
                    }
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                }
                social_proof_logos_section {
                    document {
                        ... on PrismicSocialProofLogosSection {
                            data {
                                title
                                logos {
                                    logo {
                                        alt
                                        copyright
                                        url
                                        dimensions {
                                            height
                                            width
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                feature_blocks_1 {
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    body {
                        richText
                    }
                }
                inline_video {
                    video_thumbnail {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    video_id
                }
                feature_blocks_2 {
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    body {
                        richText
                    }
                    cta_link {
                        ...PrismicLink
                    }
                    cta_text
                }
                social_proof_logos_extended_section {
                    document {
                        ... on PrismicSocialProofLogosExtendedSection {
                            data {
                                title {
                                    richText
                                }
                                logos {
                                    logo {
                                        alt
                                        copyright
                                        url
                                        dimensions {
                                            height
                                            width
                                        }
                                    }
                                }
                                cta_text
                            }
                        }
                    }
                }
                social_proof_ratings_section {
                    document {
                        ... on PrismicSocialProofRatingsSection {
                            data {
                                title {
                                    richText
                                }
                                ratings {
                                    rating {
                                        document {
                                            ... on PrismicRating {
                                                data {
                                                    logo {
                                                        alt
                                                        copyright
                                                        url
                                                        dimensions {
                                                            height
                                                            width
                                                        }
                                                    }
                                                    stars
                                                    rating_number
                                                    link
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                signup_banner {
                    document {
                        ...SignupBanner
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(About)
